import React, { useCallback, useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    Button,
    TextField,
    Typography,
    Autocomplete,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Chip,
    Alert,
    FormHelperText,
    FormControlLabel,
    Checkbox,
    Divider,
    Paper,
    Grid
} from '@mui/material';

import InfoIcon from '@mui/icons-material/Info';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PersonIcon from '@mui/icons-material/Person';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import FitnessCenter from '@mui/icons-material/FitnessCenter';
import ArticleIcon from '@mui/icons-material/Article';
import PaymentIcon from '@mui/icons-material/Payment';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PercentIcon from '@mui/icons-material/Percent';

import { createContract, getLatestContractTemplate, updateContract } from '../api/contractApi';
import { searchUsers } from '../api/membersApi';
import TextareaAutosize from "@mui/material/TextareaAutosize";
import { getFamilyMembersContract } from '../api/membersApi';
import { getAffiliateById } from '../api/affiliateApi';
import { formatCurrency } from '../utils/currency';

export default function CreateContract({
    open,
    onClose,
    affiliateId,
    contractToEdit,
    initialUser = null
}) {
    // Kasutaja otsing
    const [userQuery, setUserQuery] = useState('');
    const [userOptions, setUserOptions] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [loading, setLoading] = useState(false);

    // Lepingu põhiandmed
    const [contractType, setContractType] = useState('Monthly Membership');
    const [content, setContent] = useState('');

    const [includeFamilyMember, setIncludeFamilyMember] = useState(false);
    const [selectedFamilyMember, setSelectedFamilyMember] = useState(null);
    const [familyMemberQuery, setFamilyMemberQuery] = useState('');
    const [familyMemberOptions, setFamilyMemberOptions] = useState([]);

    // Training types
    const [trainingTypes, setTrainingTypes] = useState([]);
    const trainingTypeOptions = [
        'All classes',
        'WOD',
        'Weightlifting',
        'Rowing',
        'Kids',
        'Gymnastics',
        'Open Gym',
        'Cardio',
        'Hyrox',
        'Basic',
        'Canoeing',
    ];

    // Payment fields
    const [paymentType, setPaymentType] = useState('');
    const [paymentAmount, setPaymentAmount] = useState('');
    const [paymentInterval, setPaymentInterval] = useState('month');
    const [paymentDay, setPaymentDay] = useState(1);
    const [validUntil, setValidUntil] = useState('');
    const [startDate, setStartDate] = useState('');
    const [firstPaymentAmount, setFirstPaymentAmount] = useState('');
    const [isFirstPayment, setIsFirstPayment] = useState(true);
    const [calculationComplete, setCalculationComplete] = useState(false);

    // NEW: Discount fields
    const [discountMonths, setDiscountMonths] = useState('');
    const [discountPercent, setDiscountPercent] = useState('');

    // Dialog states
    const [successDialogOpen, setSuccessDialogOpen] = useState(false);
    const [errorDialogOpen, setErrorDialogOpen] = useState(false);
    const [dialogMessage, setDialogMessage] = useState('');
    const [validationError, setValidationError] = useState('');

    // First payment calculation preview
    const [paymentPreview, setPaymentPreview] = useState(null);
    const [paymentCurrency, setPaymentCurrency] = useState('EUR');
    const formatMoney = (value) => formatCurrency(value, paymentCurrency);

    // Add error state tracking for all fields
    const [errors, setErrors] = useState({
        selectedUser: false,
        contractType: false,
        content: false,
        paymentType: false,
        paymentAmount: false,
        paymentInterval: false,
        paymentDay: false,
        startDate: false,
        validUntil: false,
        trainingTypes: false,
        firstPaymentAmount: false,
        selectedFamilyMember: false,
        discountMonths: false,
        discountPercent: false
    });

    // Load contract data if editing or template if creating
    useEffect(() => {
        if (open && contractToEdit) {
            // Pre-fill form with contract data
            setSelectedUser({
                id: contractToEdit.userId,
                fullName: contractToEdit.user?.fullName || 'Unknown User'
            });
            setContractType(contractToEdit.contractType || 'Monthly Membership');
            setContent(contractToEdit.content || '');
            setPaymentType(contractToEdit.paymentType || '');
            setPaymentAmount(contractToEdit.paymentAmount?.toString() || '');
            setPaymentInterval(contractToEdit.paymentInterval || 'month');
            setPaymentDay(contractToEdit.paymentDay || 1);
            setStartDate(contractToEdit.startDate ? new Date(contractToEdit.startDate).toISOString().split('T')[0] : '');
            setIsFirstPayment(contractToEdit.isFirstPayment !== undefined ? contractToEdit.isFirstPayment : true);
            setFirstPaymentAmount(contractToEdit.firstPaymentAmount?.toString() || '');
            setCalculationComplete(!!contractToEdit.firstPaymentAmount);

            // Load discount fields if they exist
            setDiscountMonths(contractToEdit.discountMonths?.toString() || '');
            setDiscountPercent(contractToEdit.discountPercent?.toString() || '');

            // Format the validUntil date for the input field
            if (contractToEdit.validUntil) {
                const date = new Date(contractToEdit.validUntil);
                setValidUntil(date.toISOString().split('T')[0]);
            } else {
                setValidUntil('');
            }

            // Parse training types
            try {
                if (contractToEdit.trainingType) {
                    const trainingTypesArray = typeof contractToEdit.trainingType === 'string'
                        ? JSON.parse(contractToEdit.trainingType)
                        : contractToEdit.trainingType;
                    setTrainingTypes(trainingTypesArray);
                } else {
                    setTrainingTypes([]);
                }
            } catch (e) {
                const types = contractToEdit.trainingType?.split(',') || [];
                setTrainingTypes(types.map(t => t.trim()).filter(t => t));
            }

            resetErrors();
        } else if (open && !contractToEdit) {
            // Reset form for new contract
            setSelectedUser(initialUser);
            setUserOptions(initialUser ? [initialUser] : []);
            setUserQuery(initialUser?.fullName || '');
            setContractType('Monthly Membership');
            setPaymentType('');
            setPaymentAmount('');
            setPaymentInterval('month');
            setPaymentDay(1);
            setStartDate('');
            setValidUntil('');
            setTrainingTypes([]);
            setFirstPaymentAmount('');
            setIsFirstPayment(true);
            setCalculationComplete(false);
            setPaymentPreview(null);
            setDiscountMonths('');
            setDiscountPercent('');

            resetErrors();
            loadTemplate();
        }
    }, [open, contractToEdit, initialUser]);

    useEffect(() => {
        const loadAffiliateCurrency = async () => {
            if (!affiliateId) return;
            try {
                const affiliate = await getAffiliateById(affiliateId);
                setPaymentCurrency(affiliate?.paymentCurrency || 'EUR');
            } catch (error) {
                setPaymentCurrency('EUR');
            }
        };

        if (open) {
            loadAffiliateCurrency();
        }
    }, [open, affiliateId]);

    // Reset all error states
    const resetErrors = () => {
        setErrors({
            selectedUser: false,
            contractType: false,
            content: false,
            paymentType: false,
            paymentAmount: false,
            paymentInterval: false,
            paymentDay: false,
            startDate: false,
            validUntil: false,
            trainingTypes: false,
            firstPaymentAmount: false,
            selectedFamilyMember: false,
            discountMonths: false,
            discountPercent: false
        });
        setValidationError('');
    };

    const addYearsToDateInput = (isoDate, years) => {
        const today = new Date();
        const todayInput = [
            today.getFullYear(),
            String(today.getMonth() + 1).padStart(2, '0'),
            String(today.getDate()).padStart(2, '0'),
        ].join('-');
        const base = /^\d{4}-\d{2}-\d{2}$/.test(isoDate || '') ? isoDate : todayInput;
        const [year, month, day] = base.split('-').map(Number);
        const result = new Date(Date.UTC(year + years, month - 1, 1));
        const lastDayOfMonth = new Date(Date.UTC(
            result.getUTCFullYear(),
            result.getUTCMonth() + 1,
            0
        )).getUTCDate();
        result.setUTCDate(Math.min(day, lastDayOfMonth));
        return result.toISOString().slice(0, 10);
    };

    const setValidUntilFromYears = (years) => {
        setValidUntil(addYearsToDateInput(startDate, years));
        if (errors.validUntil) {
            setErrors((prev) => ({ ...prev, validUntil: false }));
        }
    };

    // Laeme default contract template, kui modal avaneb
    useEffect(() => {
        if (open && !contractToEdit) {
            loadTemplate();
        }
    }, [open, contractToEdit]);

    useEffect(() => {
        let active = true;
        const fetchFamilyMembers = async () => {
            if (!selectedUser) {
                setFamilyMemberOptions([]);
                return;
            }

            try {
                console.log("Fetching family members for user:", selectedUser.id);
                const results = await getFamilyMembersContract(selectedUser.id);
                console.log("Family members results:", results);
                if (active) {
                    setFamilyMemberOptions(Array.isArray(results) ? results : []);
                }
            } catch (error) {
                console.error('Error fetching family members:', error);
                if (active) {
                    setFamilyMemberOptions([]);
                }
            }
        };

        fetchFamilyMembers();
        return () => { active = false; };
    }, [selectedUser]);

    const loadTemplate = async () => {
        try {
            const template = await getLatestContractTemplate(affiliateId);
            if (template?.content) {
                setContent(template.content);
            }
        } catch (error) {
            console.error('Error loading contract template:', error);
            showErrorMessage('Failed to load contract template.');
        }
    };

    /**
     * Calculates the first payment amount for a contract based on start date and payment day
     */
    const calculateFirstPayment = useCallback(() => {
        if (!startDate || !paymentAmount || !paymentDay) {
            setFirstPaymentAmount('');
            setPaymentPreview(null);
            setCalculationComplete(false);
            return;
        }

        const paymentAmountNum = parseFloat(paymentAmount);
        if (isNaN(paymentAmountNum)) {
            setFirstPaymentAmount('');
            setPaymentPreview(null);
            setCalculationComplete(false);
            return;
        }

        // Parse start date
        const startDateObj = new Date(startDate);
        if (isNaN(startDateObj.getTime())) {
            setFirstPaymentAmount('');
            setPaymentPreview(null);
            setCalculationComplete(false);
            return;
        }

        // If payment day is same as start date day, use regular amount
        if (startDateObj.getDate() === parseInt(paymentDay)) {
            let calculatedAmount = paymentAmountNum;

            // Apply discount if it's the first month and discount is set
            if (discountMonths && discountPercent && parseInt(discountMonths) >= 1) {
                const discountAmount = (paymentAmountNum * parseFloat(discountPercent)) / 100;
                calculatedAmount = paymentAmountNum - discountAmount;
            }

            setFirstPaymentAmount(calculatedAmount.toString());
            setPaymentPreview({
                firstPaymentAmount: calculatedAmount,
                includesNextMonth: false,
                daysUntilNextPayment: 0,
                explanation: "Payment day matches contract start day, so regular monthly payment applies.",
                startDateFormatted: startDateObj.toLocaleDateString(),
                paymentDayFormatted: startDateObj.toLocaleDateString(),
                hasDiscount: discountMonths && discountPercent && parseInt(discountMonths) >= 1,
                discountAmount: discountMonths && discountPercent ? (paymentAmountNum * parseFloat(discountPercent)) / 100 : 0
            });
            setCalculationComplete(true);

            if (errors.firstPaymentAmount) {
                setErrors(prev => ({ ...prev, firstPaymentAmount: false }));
            }

            return;
        }

        // Get current month's length
        const currentMonth = startDateObj.getMonth();
        const currentYear = startDateObj.getFullYear();
        const daysInCurrentMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

        // Calculate next payment date
        let nextPaymentMonth = currentMonth;
        let nextPaymentYear = currentYear;

        if (startDateObj.getDate() > parseInt(paymentDay)) {
            nextPaymentMonth = currentMonth + 1;
            if (nextPaymentMonth > 11) {
                nextPaymentMonth = 0;
                nextPaymentYear++;
            }
        }

        const nextPaymentDate = new Date(nextPaymentYear, nextPaymentMonth, parseInt(paymentDay));

        if (nextPaymentDate.getDate() !== parseInt(paymentDay)) {
            nextPaymentDate.setDate(0);
        }

        // Calculate days until next payment date
        const msPerDay = 1000 * 60 * 60 * 24;
        const daysUntilNextPayment = Math.round((nextPaymentDate - startDateObj) / msPerDay);

        const includesNextMonth = daysUntilNextPayment < 11;

        // Calculate daily rate
        const dailyRate = paymentAmountNum / daysInCurrentMonth;

        // Calculate partial period amount
        let calculatedFirstPayment = daysUntilNextPayment * dailyRate;

        if (includesNextMonth) {
            calculatedFirstPayment += paymentAmountNum;
        }

        // Apply discount if it's applicable
        let discountAmount = 0;
        if (discountMonths && discountPercent && parseInt(discountMonths) >= 1) {
            discountAmount = (calculatedFirstPayment * parseFloat(discountPercent)) / 100;
            calculatedFirstPayment = calculatedFirstPayment - discountAmount;
        }

        calculatedFirstPayment = Math.round(calculatedFirstPayment * 100) / 100;

        setFirstPaymentAmount(calculatedFirstPayment.toString());

        if (errors.firstPaymentAmount) {
            setErrors(prev => ({ ...prev, firstPaymentAmount: false }));
        }

        // Create explanatory text for preview
        let explanation = "";
        if (includesNextMonth) {
            explanation = `First payment includes ${daysUntilNextPayment} days until next payment date (${nextPaymentDate.toLocaleDateString()}) plus the next full month because less than 10 days remain.`;
        } else {
            explanation = `First payment covers ${daysUntilNextPayment} days until next payment date (${nextPaymentDate.toLocaleDateString()}).`;
        }

        if (discountAmount > 0) {
            explanation += ` Discount applied: -${formatMoney(discountAmount)} (${discountPercent}%).`;
        }

        setPaymentPreview({
            firstPaymentAmount: calculatedFirstPayment,
            includesNextMonth,
            daysUntilNextPayment,
            nextPaymentDate: nextPaymentDate.toLocaleDateString(),
            explanation,
            startDateFormatted: startDateObj.toLocaleDateString(),
            paymentDayFormatted: nextPaymentDate.toLocaleDateString(),
            hasDiscount: discountAmount > 0,
            discountAmount
        });

        setCalculationComplete(true);
    }, [startDate, paymentAmount, paymentDay, discountMonths, discountPercent]);

    // Recalculate whenever start date, payment day, amount, or discount changes.
    useEffect(() => {
        calculateFirstPayment();
    }, [calculateFirstPayment]);

    // Show success message via dialog
    const showSuccessMessage = (message) => {
        setDialogMessage(message);
        setSuccessDialogOpen(true);
    };

    // Show error message via dialog
    const showErrorMessage = (message) => {
        setDialogMessage(message);
        setErrorDialogOpen(true);
    };

    // Otsime kasutajaid iga kord, kui userQuery muutub
    useEffect(() => {
        let active = true;
        const fetchUsers = async () => {
            if (userQuery.length === 0) {
                setUserOptions([]);
                return;
            }
            setLoading(true);
            const results = await searchUsers(userQuery);
            if (active) {
                setUserOptions(results || []);
            }
            setLoading(false);
        };

        fetchUsers();
        return () => {
            active = false;
        };
    }, [userQuery]);

    // Generic change handler that updates value and clears error
    const handleChange = (field, value) => {
        // Update the field value using the appropriate setter function
        switch (field) {
            case 'selectedUser':
                setSelectedUser(value);
                break;
            case 'contractType':
                setContractType(value);
                break;
            case 'content':
                setContent(value);
                break;
            case 'paymentType':
                setPaymentType(value);
                break;
            case 'paymentAmount':
                setPaymentAmount(value);
                break;
            case 'paymentInterval':
                setPaymentInterval(value);
                break;
            case 'paymentDay':
                setPaymentDay(value);
                break;
            case 'startDate':
                setStartDate(value);
                break;
            case 'validUntil':
                setValidUntil(value);
                break;
            case 'trainingTypes':
                setTrainingTypes(value);
                break;
            case 'firstPaymentAmount':
                setFirstPaymentAmount(value);
                break;
            case 'discountMonths':
                setDiscountMonths(value);
                break;
            case 'discountPercent':
                setDiscountPercent(value);
                break;
            default:
                break;
        }

        // Clear the error for this field
        if (errors[field]) {
            setErrors(prevErrors => ({
                ...prevErrors,
                [field]: false
            }));
        }
    };

    // Handle start date change with automatic calculation
    const handleStartDateChange = (e) => {
        const value = e.target.value;
        setStartDate(value);

        if (errors.startDate) {
            setErrors(prevErrors => ({
                ...prevErrors,
                startDate: false
            }));
        }
    };

    // Handle payment day change with automatic calculation
    const handlePaymentDayChange = (e) => {
        const value = e.target.value;
        setPaymentDay(value);

        if (errors.paymentDay) {
            setErrors(prevErrors => ({
                ...prevErrors,
                paymentDay: false
            }));
        }
    };

    // Handle payment amount change with automatic calculation
    const handlePaymentAmountChange = (e) => {
        const value = e.target.value;
        setPaymentAmount(value);

        if (errors.paymentAmount) {
            setErrors(prevErrors => ({
                ...prevErrors,
                paymentAmount: false
            }));
        }
    };

    // Handle discount fields change with automatic calculation
    const handleDiscountChange = (field, value) => {
        if (field === 'discountMonths') {
            setDiscountMonths(value);
            if (errors.discountMonths) {
                setErrors(prev => ({ ...prev, discountMonths: false }));
            }
        } else if (field === 'discountPercent') {
            setDiscountPercent(value);
            if (errors.discountPercent) {
                setErrors(prev => ({ ...prev, discountPercent: false }));
            }
        }
    };

    // Validate all form fields
    const validateForm = () => {
        const newErrors = {
            selectedUser: !selectedUser,
            contractType: !contractType,
            content: !content,
            paymentType: !paymentType,
            paymentAmount: !paymentAmount,
            paymentInterval: !paymentInterval,
            paymentDay: !paymentDay,
            startDate: !startDate,
            validUntil: !validUntil,
            trainingTypes: trainingTypes.length === 0,
            firstPaymentAmount: !firstPaymentAmount,
            selectedFamilyMember: includeFamilyMember && !selectedFamilyMember,
            // Discount validation - both fields must be filled if one is filled
            discountMonths: (discountMonths && !discountPercent) || (discountPercent && !discountMonths),
            discountPercent: (discountMonths && !discountPercent) || (discountPercent && !discountMonths)
        };

        setErrors(newErrors);

        const hasErrors = Object.values(newErrors).some(error => error);

        if (hasErrors) {
            setValidationError('Please fill in all required fields before saving.');
        } else {
            setValidationError('');
        }

        return !hasErrors;
    };

    // Lepingu salvestamine
    const handleSave = async () => {
        const isValid = validateForm();

        if (!isValid) {
            return;
        }

        try {
            const payload = {
                affiliateId,
                userId: selectedUser.id,
                contractType,
                content,
                paymentType,
                paymentAmount: paymentAmount ? parseFloat(paymentAmount) : null,
                paymentInterval,
                paymentDay: paymentDay ? parseInt(paymentDay, 10) : null,
                startDate,
                validUntil,
                trainingTypes,
                isFirstPayment: true,
                firstPaymentAmount: firstPaymentAmount ? parseFloat(firstPaymentAmount) : null,
                status: "draft",
                isFamilyMemberContract: includeFamilyMember,
                familyMemberContractId: selectedFamilyMember?.id || null,
                // Add discount fields to payload
                discountMonths: discountMonths ? parseInt(discountMonths) : null,
                discountPercent: discountPercent ? parseFloat(discountPercent) : null,
            };

            let result;
            if (contractToEdit) {
                payload.isFirstPayment = isFirstPayment;
                result = await updateContract(contractToEdit.id, payload);
                showSuccessMessage('Contract updated successfully!');
            } else {
                result = await createContract(payload);
                showSuccessMessage('Contract created successfully!');
            }

            setTimeout(() => {
                setSuccessDialogOpen(false);
                onClose();
            }, 1500);
        } catch (error) {
            console.error(contractToEdit ? 'Error updating contract:' : 'Error creating contract:', error);
            showErrorMessage(contractToEdit ? 'Failed to update contract. Please try again.' : 'Failed to create contract. Please try again.');
        }
    };

    const handleTrainingTypesChange = (event, newValue) => {
        setTrainingTypes(newValue);

        if (newValue.length > 0 && errors.trainingTypes) {
            setErrors(prev => ({ ...prev, trainingTypes: false }));
        }
    };

    // Improved helper component tooltip
    const InfoTooltip = ({ title }) => {
        const [isOpen, setIsOpen] = useState(false);

        return (
            <Box
                component="span"
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    mr: 1,
                    position: 'relative',
                }}
                onMouseEnter={() => setIsOpen(true)}
                onMouseLeave={() => setIsOpen(false)}
            >
                <InfoIcon fontSize="small" color="action" sx={{ fontSize: '16px' }} />
                {isOpen && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '-40px',
                            left: '50%',
                            backgroundColor: 'rgba(97, 97, 97, 0.9)',
                            color: 'white',
                            padding: '6px 10px',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            maxWidth: '250px',
                            whiteSpace: 'normal',
                            zIndex: 1000,
                            boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
                        }}
                    >
                        {title}
                    </Box>
                )}
            </Box>
        );
    };

    // Section Header Component
    const SectionHeader = ({ icon: Icon, title, subtitle }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 3 }}>
            <Icon sx={{ mr: 2, color: 'primary.main' }} />
            <Box>
                <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
                    {title}
                </Typography>
                {subtitle && (
                    <Typography variant="body2" color="text.secondary">
                        {subtitle}
                    </Typography>
                )}
            </Box>
        </Box>
    );
    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{contractToEdit ? 'Edit Contract' : 'Create Contract'}</DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    {contractToEdit
                        ? 'Edit the contract details below. All fields are required.'
                        : 'Fill the fields to create a new contract. All fields are required.'}
                </Typography>

                {/* Display validation error if any */}
                {validationError && (
                    <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
                        {validationError}
                    </Alert>
                )}

                {/* SECTION 1: USER SELECTION */}
                <Paper elevation={1} sx={{ px: 1, mb: 0 }}>
                    <SectionHeader
                        icon={PersonIcon}
                        title="Member Selection"
                        subtitle="Choose the member for this contract"
                    />

                    {/* User search */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <InfoTooltip title="Search for a member" />
                        <Autocomplete
                            fullWidth
                            options={userOptions}
                            getOptionLabel={(option) => option.fullName || ''}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            value={selectedUser}
                            onChange={(event, newValue) => {
                                setSelectedUser(newValue);
                                if (newValue && errors.selectedUser) {
                                    setErrors(prev => ({ ...prev, selectedUser: false }));
                                }
                            }}
                            onInputChange={(event, newInputValue) => {
                                setUserQuery(newInputValue);
                            }}
                            loading={loading}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Search User by fullName *"
                                    error={errors.selectedUser}
                                    helperText={errors.selectedUser ? "User selection is required" : ""}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                    disabled={!!contractToEdit}
                                />
                            )}
                            disabled={!!contractToEdit}
                        />
                    </Box>

                    {/* Family Member Section */}
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={includeFamilyMember}
                                onChange={(e) => {
                                    setIncludeFamilyMember(e.target.checked);
                                    if (!e.target.checked) {
                                        setSelectedFamilyMember(null);
                                        setFamilyMemberQuery('');
                                    }
                                }}
                                disabled={!selectedUser}
                            />
                        }
                        label="Include family member?"
                        sx={{ mb: 2 }}
                    />

                    {includeFamilyMember && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <InfoTooltip title="Search for a family member" />
                            <Autocomplete
                                fullWidth
                                options={familyMemberOptions || []}
                                getOptionLabel={(option) => option.fullName || ''}
                                value={selectedFamilyMember}
                                onChange={(event, newValue) => {
                                    setSelectedFamilyMember(newValue);
                                    if (newValue && errors.selectedFamilyMember) {
                                        setErrors(prev => ({ ...prev, selectedFamilyMember: false }));
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Search Family Member *"
                                        error={errors.selectedFamilyMember}
                                        helperText={errors.selectedFamilyMember ? "Family member selection is required when checkbox is checked" : ""}
                                    />
                                )}
                            />
                        </Box>
                    )}
                </Paper>

                {/* SECTION 2: CONTRACT DETAILS */}
                <Paper elevation={1} sx={{ px: 1, mb: 0 }}>
                    <SectionHeader
                        icon={ArticleIcon}
                        title="Contract Details"
                        subtitle="Basic contract information"
                    />

                    {/* Contract type */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <InfoTooltip title="Contract name" />
                        <TextField
                            label="Contract Name *"
                            value={contractType}
                            onChange={(e) => handleChange('contractType', e.target.value)}
                            fullWidth
                            error={errors.contractType}
                            helperText={errors.contractType ? "Contract name is required" : ""}
                        />
                    </Box>

                    {/* Training Types */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <InfoTooltip title="Select available training types" />
                        <Autocomplete
                            multiple
                            fullWidth
                            options={trainingTypeOptions}
                            value={trainingTypes}
                            onChange={handleTrainingTypesChange}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip
                                        label={option}
                                        size="small"
                                        {...getTagProps({ index })}
                                    />
                                ))
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Training Types *"
                                    placeholder="Select training types"
                                    error={errors.trainingTypes}
                                    helperText={errors.trainingTypes ? "At least one training type is required" : ""}
                                />
                            )}
                        />
                    </Box>
                </Paper>

                {/* SECTION 3: PAYMENT SETTINGS */}
                <Paper elevation={1} sx={{ px: 1, mb: 0 }}>
                    <SectionHeader
                        icon={PaymentIcon}
                        title="Payment Settings"
                        subtitle="Configure payment amounts and schedule"
                    />

                    <Grid container spacing={2}>
                        {/* Payment Type */}
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <InfoTooltip title="Type of payment" />
                                <TextField
                                    label="Payment Type *"
                                    value={paymentType}
                                    onChange={(e) => handleChange('paymentType', e.target.value)}
                                    fullWidth
                                    error={errors.paymentType}
                                    helperText={errors.paymentType ? "Payment type is required" : ""}
                                />
                            </Box>
                        </Grid>

                        {/* Payment Amount */}
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <InfoTooltip title="Regular monthly payment amount. If you want to receive transactions outside this application, then put it 0." />
                                <TextField
                                    label="Monthly Payment Amount *"
                                    type="number"
                                    value={paymentAmount}
                                    onChange={handlePaymentAmountChange}
                                    fullWidth
                                    error={errors.paymentAmount}
                                    helperText={errors.paymentAmount ? "Payment amount is required" : ""}
                                />
                            </Box>
                        </Grid>

                        {/* Payment Interval */}
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <InfoTooltip title="Frequency of payments" />
                                <FormControl fullWidth error={errors.paymentInterval}>
                                    <InputLabel id="payment-interval-label">Payment Interval *</InputLabel>
                                    <Select
                                        labelId="payment-interval-label"
                                        label="Payment Interval *"
                                        value={paymentInterval}
                                        onChange={(e) => handleChange('paymentInterval', e.target.value)}
                                    >
                                        <MenuItem value="month">Month</MenuItem>
                                    </Select>
                                    {errors.paymentInterval && (
                                        <FormHelperText error>Payment interval is required</FormHelperText>
                                    )}
                                </FormControl>
                            </Box>
                        </Grid>

                        {/* Payment Day */}
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <InfoTooltip title="Day of payment (1-28)" />
                                <TextField
                                    label="Payment Day *"
                                    type="number"
                                    inputProps={{ min: 1, max: 28 }}
                                    value={paymentDay}
                                    onChange={handlePaymentDayChange}
                                    fullWidth
                                    error={errors.paymentDay}
                                    helperText={errors.paymentDay ? "Payment day is required" : ""}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>

                {/* SECTION 4: DISCOUNT SETTINGS */}
                <Paper elevation={1} sx={{ px: 1, mb: 0 }}>
                    <SectionHeader
                        icon={PercentIcon}
                        title="Discount Settings"
                        subtitle="Optional discount for initial payments"
                    />

                    <Grid container spacing={2}>
                        {/* Discount Months */}
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <InfoTooltip title="Number of payments the discount applies (starting from the first payment)" />
                                <TextField
                                    label="Discount Duration (payments)"
                                    type="number"
                                    value={discountMonths}
                                    onChange={(e) => handleDiscountChange('discountMonths', e.target.value)}
                                    fullWidth
                                    inputProps={{ min: 0, max: 12 }}
                                    error={errors.discountMonths}
                                    helperText={errors.discountMonths ? "Both discount fields must be filled if using discount" : "Leave empty for no discount"}
                                />
                            </Box>
                        </Grid>

                        {/* Discount Percentage */}
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <InfoTooltip title="Percentage of discount to apply (0-100)" />
                                <TextField
                                    label="Discount Percentage (%)"
                                    type="number"
                                    value={discountPercent}
                                    onChange={(e) => handleDiscountChange('discountPercent', e.target.value)}
                                    fullWidth
                                    inputProps={{ min: 0, max: 100, step: 0.1 }}
                                    error={errors.discountPercent}
                                    helperText={errors.discountPercent ? "Both discount fields must be filled if using discount" : "Leave empty for no discount"}
                                />
                            </Box>
                        </Grid>
                    </Grid>

                    {/* Discount Preview */}
                    {discountMonths && discountPercent && paymentAmount && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            <Typography variant="body2" fontWeight="bold">
                                Discount Preview:
                            </Typography>
                            <Typography variant="body2">
                                First {discountMonths} payment{parseInt(discountMonths) > 1 ? 's' : ''} with {discountPercent}% discount
                            </Typography>

                        </Alert>
                    )}
                </Paper>

                {/* SECTION 5: DATE SETTINGS */}
                <Paper elevation={1} sx={{ px: 1, mb: 0 }}>
                    <SectionHeader
                        icon={CalendarTodayIcon}
                        title="Contract Dates"
                        subtitle="Set contract duration and payment schedule"
                    />

                    <Grid container spacing={2}>
                        {/* Start Date */}
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <InfoTooltip title="Contract start date" />
                                <TextField
                                    label="Start Date *"
                                    type="date"
                                    value={startDate}
                                    onChange={handleStartDateChange}
                                    fullWidth
                                    error={errors.startDate}
                                    helperText={errors.startDate ? "Start date is required" : ""}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                />
                            </Box>
                        </Grid>

                        {/* Valid Until */}
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                                <InfoTooltip title="Expiration date. Use the buttons to set 1, 5 or 10 years from the start date." />
                                <Box sx={{ width: '100%' }}>
                                    <TextField
                                        label="Valid Until *"
                                        type="date"
                                        value={validUntil}
                                        onChange={(e) => handleChange('validUntil', e.target.value)}
                                        fullWidth
                                        error={errors.validUntil}
                                        helperText={errors.validUntil
                                            ? "End date is required"
                                            : "Set from the start date, or pick a date."}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                    />
                                    <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                                        {[
                                            { years: 1, label: '1 year' },
                                            { years: 5, label: '5 years' },
                                            { years: 10, label: '10 years' },
                                        ].map((option) => {
                                            const optionDate = addYearsToDateInput(startDate, option.years);
                                            const selected = validUntil === optionDate;
                                            return (
                                                <Button
                                                    key={option.years}
                                                    size="small"
                                                    variant={selected ? 'contained' : 'outlined'}
                                                    onClick={() => setValidUntilFromYears(option.years)}
                                                >
                                                    {option.label}
                                                </Button>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>

                    {/* First Payment Amount */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <InfoTooltip title="First payment amount, recalculated automatically when the start date, payment day, amount or discount changes." />
                        <TextField
                            label="First Payment Amount *"
                            type="number"
                            value={firstPaymentAmount}
                            onChange={(e) => handleChange('firstPaymentAmount', e.target.value)}
                            fullWidth
                            error={errors.firstPaymentAmount}
                            helperText={errors.firstPaymentAmount
                                ? "First payment amount is required"
                                : "Calculated automatically from start date and payment day"}
                            InputProps={{
                                readOnly: true,
                            }}
                        />
                    </Box>

                    {/* First Payment Preview */}
                    {paymentPreview && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <Typography variant="body2" fontWeight="bold">
                                First Payment Calculation:
                            </Typography>
                            <Typography variant="body2">
                                {paymentPreview.explanation}
                            </Typography>
                            {paymentPreview.hasDiscount && (
                                <Typography variant="body2" sx={{ mt: 1, color: 'success.main' }}>
                                    <strong>Discount applied:</strong> -{formatMoney(paymentPreview.discountAmount)} ({discountPercent}%)
                                </Typography>
                            )}
                            {paymentPreview.includesNextMonth && (
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    <strong>Period 1:</strong> {paymentPreview.startDateFormatted} to {paymentPreview.paymentDayFormatted} - {paymentPreview.daysUntilNextPayment} days
                                    <br />
                                    <strong>Period 2:</strong> Full next month
                                    <br />
                                    <strong>Total amount:</strong> {formatMoney(paymentPreview.firstPaymentAmount)}
                                </Typography>
                            )}
                        </Alert>
                    )}
                </Paper>

                {/* SECTION 6: CONTRACT CONTENT */}
                <Paper elevation={1} sx={{ px: 1, mb: 0 }}>
                    <SectionHeader
                        icon={ArticleIcon}
                        title="Contract Content"
                        subtitle="Legal terms and conditions"
                    />

                    {/* Contract Content */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                        <InfoTooltip title="Contract text content" />
                        <FormControl fullWidth error={errors.content}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>Contract Content *</Typography>
                            <TextareaAutosize
                                minRows={10}
                                value={content}
                                onChange={(e) => handleChange('content', e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '4px',
                                    borderColor: errors.content ? '#d32f2f' : '#AAAAAA',
                                    fontSize: '14px',
                                    lineHeight: '1.5',
                                    resize: 'vertical',
                                    fontFamily: 'inherit'
                                }}
                            />
                            {errors.content && (
                                <FormHelperText error>Contract content is required</FormHelperText>
                            )}
                        </FormControl>
                    </Box>
                </Paper>

            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} color="inherit" variant="outlined">
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    color="primary"
                >
                    {contractToEdit ? 'Update Contract' : 'Save Contract'}
                </Button>
            </DialogActions>

            {/* Success Dialog */}
            <Dialog
                open={successDialogOpen}
                onClose={() => setSuccessDialogOpen(false)}
            >
                <DialogTitle>Success</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {dialogMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSuccessDialogOpen(false)} color="primary">
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Error Dialog */}
            <Dialog
                open={errorDialogOpen}
                onClose={() => setErrorDialogOpen(false)}
            >
                <DialogTitle>Error</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {dialogMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setErrorDialogOpen(false)} color="primary">
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
}